/**
 * PlanSelector — a real plan-picker (tier cards + interval toggle). Renders
 * either as a modal overlay (default — used by InlinePlanModal on the Dashboard
 * / QR page) or **inline** as an in-page panel (used on /account/billing, where
 * a modal on top of the dedicated billing page would be redundant).
 *
 * a11y: modal variant = role="dialog" + aria-modal + ESC/backdrop close + focus
 * in; inline variant = role="region". Interval toggle is a radiogroup, current
 * plan marked with text, CTAs >= 44px.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import type { BillingInterval, CatalogProduct, ProductTier } from "@d-m-organization/pricing-ui";
import { formatEuro } from "../lib/formatEuro.js";
import { useTranslation } from "react-i18next";

const C = {
  navy: "#261F59", purple: "#351DBD", purpleLight: "#EDE9FC", orange: "#FF861D",
  textStrong: "#2b2e44", textMuted: "#6b7084", muted: "#E3EBF6", white: "#fff",
  bgTint: "#F7F6FB", green: "#166534", greenBg: "#e9fbe9", errorText: "#9b1c1c",
};

const ORDER: ProductTier[] = ["free", "pro", "business"];
const TAGLINE: Record<ProductTier, string> = {
  free: "planSelector.taglineFree",
  pro: "planSelector.taglinePro",
  business: "planSelector.taglineBusiness",
};

function tierLabel(t: string): string { return t.charAt(0).toUpperCase() + t.slice(1); }

function savingsPercent(product: CatalogProduct): number {
  const t = product.tiers.pro?.active ? product.tiers.pro : product.tiers.business;
  if (!t || !t.monthlyPriceCents) return 0;
  const yearly = t.monthlyPriceCents * 12;
  if (yearly <= 0) return 0;
  return Math.round((1 - t.annualPriceCents / yearly) * 100);
}

export type PlanSelectorProps = {
  readonly product: CatalogProduct;
  readonly currentTier: ProductTier;
  readonly currentInterval: BillingInterval | null;
  readonly initialInterval?: BillingInterval;
  readonly initialTier?: "pro" | "business";
  readonly busy?: boolean;
  readonly error?: string | null;
  /** "modal" (default) renders an overlay dialog; "inline" renders an in-page panel. */
  readonly variant?: "modal" | "inline";
  readonly onChoose: (tier: "pro" | "business", interval: BillingInterval) => void;
  readonly onClose: () => void;
};

export function PlanSelector(props: PlanSelectorProps) {
  const { t } = useTranslation();
  const { product, currentTier, currentInterval, busy, error } = props;
  const inline = props.variant === "inline";
  const [interval, setInterval] = useState<BillingInterval>(
    props.initialInterval ?? (currentInterval ?? "month"),
  );
  const titleId = useRef("plan-sel-" + Math.random().toString(36).slice(2)).current;
  const firstFocus = useRef<HTMLButtonElement | null>(null);
  const triggerRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    triggerRef.current = (typeof document !== "undefined" ? document.activeElement : null) as HTMLElement | null;
    firstFocus.current?.focus();
    if (inline) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") props.onClose(); };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      triggerRef.current?.focus?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tiers = useMemo(() => ORDER.filter((t) => product.tiers[t]?.active), [product]);
  const savePct = savingsPercent(product);

  const body = (
    <>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
        <div>
          <h2 id={titleId} style={{ fontSize: 20, fontWeight: 800, color: C.navy, margin: "0 0 4px", letterSpacing: "-0.02em" }}>
            {t("planSelector.title", { planName: product.displayName })}
          </h2>
          <p style={{ fontSize: 13, color: C.textMuted, margin: 0 }}>{t("planSelector.subtitle")}</p>
        </div>
        <button type="button" aria-label={t("planSelector.close")} onClick={props.onClose} style={{ font: "inherit", fontSize: 22, lineHeight: 1, width: 36, height: 36, borderRadius: 8, border: "1px solid " + C.muted, background: C.white, color: C.textMuted, cursor: "pointer" }}>×</button>
      </div>

      <div role="radiogroup" aria-label={t("planSelector.billingIntervalLabel")} style={{ display: "inline-flex", background: C.bgTint, border: "1px solid " + C.muted, borderRadius: 9999, padding: 4, margin: "16px 0 18px" }}>
        {(["month", "year"] as BillingInterval[]).map((iv) => {
          const active = interval === iv;
          return (
            <button key={iv} type="button" role="radio" aria-checked={active} onClick={() => setInterval(iv)}
              style={{ font: "inherit", fontSize: 13, fontWeight: 600, minHeight: 36, padding: "6px 16px", borderRadius: 9999, border: 0, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 8, background: active ? C.navy : "transparent", color: active ? C.white : C.textMuted }}>
              {iv === "year" ? t("planSelector.yearly") : t("planSelector.monthly")}
              {iv === "year" && savePct > 0 && (
                <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 6px", borderRadius: 4, background: active ? "rgba(255,134,29,0.25)" : "#FFF0E2", color: active ? "#FFD2A8" : C.orange }}>{t("planSelector.savingsBadge", { pct: savePct })}</span>
              )}
            </button>
          );
        })}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 14 }}>
        {tiers.map((tier) => {
          const info = product.tiers[tier]!;
          const isFree = tier === "free";
          const perMonth = isFree ? 0 : interval === "year" ? Math.round(info.annualPriceCents / 12) : info.monthlyPriceCents;
          const isCurrent = tier === currentTier && (isFree || interval === currentInterval);
          const recommended = tier === "pro" && !isCurrent;
          const preselect = props.initialTier === tier;
          return (
            <div key={tier} style={{ position: "relative", border: "1px solid " + (recommended || preselect ? C.purple : C.muted), background: recommended ? C.purpleLight + "55" : C.white, borderRadius: 14, padding: "18px 16px 16px", display: "flex", flexDirection: "column", gap: 10, boxShadow: recommended ? "0 8px 24px rgba(53,29,189,0.12)" : "none" }}>
              {recommended && (
                <span style={{ position: "absolute", top: -10, left: 14, fontSize: 10, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: C.white, background: C.purple, padding: "3px 8px", borderRadius: 6 }}>{t("planSelector.recommended")}</span>
              )}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <h3 style={{ fontSize: 15, fontWeight: 800, color: C.navy, margin: 0 }}>{info.displayName || tierLabel(tier)}</h3>
                {isCurrent && (<span style={{ fontSize: 10, fontWeight: 700, color: C.green, background: C.greenBg, padding: "3px 7px", borderRadius: 5 }}>{t("planSelector.yourPlan")}</span>)}
              </div>
              <p style={{ fontSize: 12, color: C.textMuted, margin: 0, minHeight: 32 }}>{t(TAGLINE[tier])}</p>
              <div style={{ display: "flex", alignItems: "baseline", gap: 3 }}>
                {isFree ? (
                  <span style={{ fontSize: 24, fontWeight: 800, color: C.navy }}>{t("planSelector.free")}</span>
                ) : (
                  <>
                    <span style={{ fontSize: 16, fontWeight: 700, color: C.navy, alignSelf: "flex-start", marginTop: 3 }}>€</span>
                    <span style={{ fontSize: 28, fontWeight: 800, color: C.navy, lineHeight: 1 }}>{formatEuro(perMonth)}</span>
                    <span style={{ fontSize: 12, color: C.textMuted, marginLeft: 2 }}>{t("planSelector.perMonth")}</span>
                  </>
                )}
              </div>
              {!isFree && interval === "year" && (<div style={{ fontSize: 11, color: C.textMuted, marginTop: -4 }}>{t("planSelector.billedAnnually", { amount: formatEuro(info.annualPriceCents) })}</div>)}
              <div style={{ marginTop: "auto", paddingTop: 6 }}>
                {isCurrent ? (
                  <button type="button" disabled aria-disabled="true" style={{ ...ctaBase, background: C.bgTint, color: C.textMuted, border: "1px solid " + C.muted, cursor: "default" }}>{t("planSelector.currentPlan")}</button>
                ) : isFree ? (
                  <span style={{ fontSize: 12, color: C.textMuted }}>{t("planSelector.includedByDefault")}</span>
                ) : (
                  <button type="button" ref={preselect ? firstFocus : undefined} disabled={busy} onClick={() => props.onChoose(tier as "pro" | "business", interval)}
                    style={{ ...ctaBase, background: recommended || preselect ? C.purple : C.white, color: recommended || preselect ? C.white : C.navy, border: "1px solid " + (recommended || preselect ? "transparent" : C.muted), opacity: busy ? 0.6 : 1, cursor: busy ? "default" : "pointer" }}>
                    {busy ? t("planSelector.working") : t("planSelector.chooseTier", { tier: info.displayName || tierLabel(tier) })}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {error && <p role="alert" style={{ color: C.errorText, fontSize: 13, margin: "14px 0 0" }}>{error}</p>}
    </>
  );

  if (inline) {
    return (
      <section role="region" aria-labelledby={titleId} style={{ background: C.white, border: "1px solid " + C.muted, borderRadius: 16, padding: "22px 22px 24px", marginTop: 20 }}>
        {body}
      </section>
    );
  }

  return (
    <div onClick={(e) => { if (e.target === e.currentTarget) props.onClose(); }}
      style={{ position: "fixed", inset: 0, background: "rgba(38,31,89,0.45)", display: "grid", placeItems: "center", zIndex: 1000, padding: 16, overflowY: "auto" }}>
      <div role="dialog" aria-modal="true" aria-labelledby={titleId} style={{ background: C.white, borderRadius: 18, padding: "26px 26px 28px", maxWidth: 760, width: "100%", boxShadow: "0 24px 60px rgba(38,31,89,0.30)" }}>
        {body}
      </div>
    </div>
  );
}

const ctaBase: React.CSSProperties = { font: "inherit", fontSize: 13, fontWeight: 700, width: "100%", minHeight: 44, padding: "9px 14px", borderRadius: 9 };
